// Google Analytics 4 configuration
import ReactGA from 'react-ga4';

// Initialize Google Analytics
export const initGA = () => {
  const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
  
  if (GA_MEASUREMENT_ID) {
    ReactGA.initialize(GA_MEASUREMENT_ID, {
      debug: process.env.NODE_ENV === 'development',
      gtagOptions: {
        // Enhanced measurement settings
        send_page_view: true,
        allow_google_signals: true,
        allow_ad_personalization_signals: true,
        // Cookie settings
        cookie_flags: 'SameSite=None;Secure',
        // Custom parameters
        custom_map: {
          'custom_parameter_1': 'automation_type'
        }
      }
    });

    // Set default parameters
    ReactGA.gtag('config', GA_MEASUREMENT_ID, {
      page_title: document.title,
      page_location: window.location.href,
      // SEO and marketing tags
      content_group1: 'Automation Platform',
      content_group2: 'Enterprise Software',
      // User properties
      user_properties: {
        platform: 'web'
      }
    });

    console.log('Google Analytics initialized');
  } else {
    // Google Analytics not configured - silent fallback
    // console.warn('Google Analytics measurement ID not found');
  }
};

// Track page views
export const trackPageView = (path, title) => {
  ReactGA.send({ 
    hitType: 'pageview', 
    page: path,
    title: title,
    // Additional SEO tracking
    page_title: title,
    page_location: window.location.origin + path
  });
};

// Track custom events
export const trackEvent = (eventName, parameters = {}) => {
  ReactGA.event(eventName, {
    // Default parameters
    engagement_time_msec: parameters.engagement_time || 0,
    // Custom parameters
    ...parameters
  });
};

// Track conversion events for SEO
export const trackConversion = (action, category = 'conversion', value = 0) => {
  ReactGA.event('conversion', {
    event_category: category,
    event_label: action,
    value: value,
    // Enhanced ecommerce for SaaS
    currency: 'USD',
    // Custom dimensions for B2B tracking
    automation_type: category,
    user_tier: 'prospect'
  });
};

// Track search interactions for SEO insights
export const trackSearch = (searchTerm, resultsCount = 0) => {
  ReactGA.event('search', {
    search_term: searchTerm,
    event_category: 'site_search',
    custom_parameter_search_results: resultsCount
  });
};

// Track user engagement for SEO metrics
export const trackEngagement = (action, element, value = 1) => {
  ReactGA.event('engagement', {
    event_category: 'user_engagement',
    event_label: element,
    action: action,
    value: value,
    // Time-based engagement
    engagement_time_msec: Date.now(),
    // Page context
    page_title: document.title,
    page_location: window.location.href
  });
};

// Track form interactions (important for conversion tracking)
export const trackFormEvent = (formName, action, field = '') => {
  ReactGA.event('form_interaction', {
    event_category: 'form',
    event_label: formName,
    action: action,
    field_name: field,
    // Form completion tracking
    form_name: formName,
    form_destination: window.location.pathname
  });
};

// Track video interactions (if applicable)
export const trackVideoEvent = (action, videoTitle, progress = 0) => {
  ReactGA.event('video', {
    event_category: 'video_engagement',
    event_label: videoTitle,
    action: action,
    video_title: videoTitle,
    video_percent: progress,
    // SEO value metrics
    content_type: 'video',
    engagement_type: 'media'
  });
};

// Track scroll depth for content engagement
export const trackScrollDepth = (percentage) => {
  ReactGA.event('scroll', {
    event_category: 'content_engagement',
    event_label: `${percentage}%`,
    scroll_depth: percentage,
    // Page context for content optimization
    page_title: document.title,
    content_length: document.body.scrollHeight
  });
};

// Enhanced ecommerce tracking for SaaS
export const trackPurchase = (transactionId, items = [], value = 0) => {
  ReactGA.event('purchase', {
    transaction_id: transactionId,
    value: value,
    currency: 'USD',
    items: items.map(item => ({
      item_id: item.id,
      item_name: item.name,
      item_category: 'subscription',
      item_variant: item.tier,
      price: item.price,
      quantity: 1
    })),
    // B2B specific tracking
    customer_lifetime_value: value * 12, // Annual value
    acquisition_channel: 'organic_search'
  });
};

// Track signup conversions
export const trackSignup = (method = 'email', tier = 'free') => {
  ReactGA.event('sign_up', {
    method: method,
    event_category: 'conversion',
    subscription_tier: tier,
    // Lead scoring for B2B
    lead_score: tier === 'enterprise' ? 100 : tier === 'business' ? 75 : 50,
    conversion_type: 'trial_signup'
  });
};

// Track trial-to-paid conversion
export const trackUpgrade = (fromTier, toTier, value) => {
  ReactGA.event('upgrade', {
    event_category: 'conversion',
    from_tier: fromTier,
    to_tier: toTier,
    upgrade_value: value,
    // Customer success metrics
    trial_length: 14, // days
    conversion_type: 'trial_to_paid'
  });
};

export default {
  initGA,
  trackPageView,
  trackEvent,
  trackConversion,
  trackSearch,
  trackEngagement,
  trackFormEvent,
  trackVideoEvent,
  trackScrollDepth,
  trackPurchase,
  trackSignup,
  trackUpgrade
};